/**
 * WashPros i18n (Internationalization) Configuration
 * Supports 10 Languages: English, Spanish, French, German, Portuguese, Chinese, Japanese, Korean, Italian, Russian
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import zh from './locales/zh.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import it from './locales/it.json';
import ru from './locales/ru.json';
import ht from './locales/ht.json';
import { mediaGalleryTranslations } from './locales/mediaGallery';
import { serviceAreaMapTranslations } from './locales/serviceAreaMap';

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  pt: { translation: pt },
  zh: { translation: zh },
  ja: { translation: ja },
  ko: { translation: ko },
  it: { translation: it },
  ru: { translation: ru },
  ht: { translation: ht }
};

Object.entries(mediaGalleryTranslations).forEach(([language, strings]) => {
  resources[language].translation.media_gallery = strings;
});
Object.entries(serviceAreaMapTranslations).forEach(([language, strings]) => {
  resources[language].translation.service_area_map = strings;
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr', 'de', 'pt', 'zh', 'ja', 'ko', 'it', 'ru', 'ht'],
    
    interpolation: {
      escapeValue: false // React already handles escaping
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'washpros-language'
    },
    
    react: {
      useSuspense: false
    }
  });

export const supportedLanguages = [
  // Phase 7g.15.72 Admin-mobile Item #2 — `abbr` field used by
  // LanguageSelector to render a 3-letter abbreviation (ENG/ESP/FRA/
  // DEU/POR/ZHO/JPN/KOR) on phone vertical where the full name does
  // not fit in the admin top header.
  { code: 'en', name: 'English', abbr: 'ENG', flag: '🇺🇸' },
  { code: 'es', name: 'Español', abbr: 'ESP', flag: '🇪🇸' },
  // Phase 7g.15.55 (2026-06-02) — non-EN/ES locales hidden pending
  // full translation coverage.  Currently <60% translated for these.
  // To re-enable: confirm all admin/vendor/customer flows pass at
  // 390×844 in the target locale, then move back into the array.
  { code: 'fr', name: 'Français', abbr: 'FRA', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', abbr: 'DEU', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', abbr: 'POR', flag: '🇧🇷' },
  { code: 'zh', name: '中文', abbr: 'ZHO', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', abbr: 'JPN', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', abbr: 'KOR', flag: '🇰🇷' },
  // Phase 7g.15.84 — Italian flipped to LIVE after full EN parity
  // achieved (3,209 keys, 47/47 i18n_parity PASS).
  { code: 'it', name: 'Italiano', abbr: 'ITA', flag: '🇮🇹' },
  // Phase 7g.15.85 — Russian flipped to LIVE after full EN parity
  // achieved (3,209 keys, 55/55 i18n_parity PASS).
  { code: 'ru', name: 'Русский', abbr: 'RUS', flag: '🇷🇺' },
  // Phase i18n expansion 2026-06-12 — Haitian Creole flipped to LIVE
  // after full EN parity (3,338 keys) + legacy-dict coverage
  // (LanguageContext / vendorPortalI18n / adminFrontPageI18n) +
  // 390×844 smoke pass. Backend chat translation already supported ht.
  { code: 'ht', name: 'Kreyòl Ayisyen', abbr: 'HAT', flag: '🇭🇹' }
];

export const changeLanguage = (langCode) => {
  i18n.changeLanguage(langCode);
  localStorage.setItem('washpros-language', langCode);
  // Dispatch event for real-time updates across components
  window.dispatchEvent(new CustomEvent('languageChange', { detail: langCode }));
};

export const getCurrentLanguage = () => {
  return i18n.language || 'en';
};

// Listen for language changes from other components
export const onLanguageChange = (callback) => {
  const handler = (event) => callback(event.detail);
  window.addEventListener('languageChange', handler);
  return () => window.removeEventListener('languageChange', handler);
};

export default i18n;
